import React from 'react';
import { DeveloperProfile, DeveloperPublicProfileUpdateInput, savePublicProfile } from './profile-graphql';
import { StyledHeaderText } from '../../../../common/Styleguide/Common/Text';
import Footer from '../../Footer/Footer';
import ContentHeader from '../../../../common/Styleguide/ContentHeader';
import { useTheme } from '@mui/system';
import Container from '../../../../common/Styleguide/Container';
import { COLORS } from '../../../../common/Styleguide/Common/colors';
import { Divider, Typography } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { toast } from 'react-toastify';
import { StyledButton } from '../../../../common/Styleguide/Common/Button';
import { GraphQLError } from '../../../../common/components/ApolloError/GraphQLError';
import Space from '../../../../common/Styleguide/Space';
import Social from './Sections/Social';
import Account from './Sections/Account';
import EmailPreferences from './Sections/EmailPreferences';

export interface ProfileProps {
  profile: DeveloperProfile;
}

export interface AccountData {
  name: string;
  nameError: string | null;
  email: string;
  verificationEmailSent: boolean;
  acceptLegal: boolean;
  loading: boolean;
}

export interface PublicProfileData {
  displayName?: string;
  displayNameError: string | null;
  tiktok?: string;
  tiktokError: string | null;
  discord?: string;
  discordError: string | null;
  facebook?: string;
  facebookError: string | null;
  instagram?: string;
  instagramError: string | null;
  twitter?: string;
  twitterError: string | null;
  youtube?: string;
  youtubeError: string | null;
  loading: boolean;
  isValid: boolean;
  validateEmailNotification: boolean;
}

const Profile: React.FC<ProfileProps> = ({ profile }) => {
  const { spacing } = useTheme();

  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<ApolloError | null>(null);

  const [publicProfileData, setPublicProfileData] = React.useState<PublicProfileData>({
    displayName: profile.publicProfile.displayName || '',
    displayNameError: null,
    tiktok: profile.publicProfile.tiktok || '',
    tiktokError: null,
    discord: profile.publicProfile.discord || '',
    discordError: null,
    facebook: profile.publicProfile.facebook || '',
    facebookError: null,
    instagram: profile.publicProfile.instagram || '',
    instagramError: null,
    twitter: profile.publicProfile.twitter || '',
    twitterError: null,
    youtube: profile.publicProfile.youtube || '',
    youtubeError: null,
    isValid: true,
    loading: false,
    validateEmailNotification: false,
  });

  const save = async () => {
    const publicProfileInput: DeveloperPublicProfileUpdateInput = {
      displayName: publicProfileData.displayName || undefined,
      tiktok: publicProfileData.tiktok || undefined,
      discord: publicProfileData.discord || undefined,
      instagram: publicProfileData.instagram || undefined,
      facebook: publicProfileData.facebook || undefined,
      twitter: publicProfileData.twitter || undefined,
      youtube: publicProfileData.youtube || undefined,
    };

    setLoading(true);
    setError(null);
    toast.loading('Saving account...');

    try {
      await savePublicProfile(publicProfileInput);
      toast.dismiss();
      toast.success('Account saved successfully!');
      setLoading(false);
    } catch (e) {
      console.error('Error saving account or public profile', JSON.stringify(e, null, 2));
      toast.dismiss();
      toast.error('Error saving account or profile', { autoClose: false });
      setLoading(false);
      setError(e as ApolloError);
    }
  };

  const renderNotification = () => {
    if (publicProfileData.validateEmailNotification) {
      return (
        <Space variant="ABOVE">
          <div>
            <Typography>You updated your contact email, you now need to verify it, check your inbox!</Typography>
          </div>
        </Space>
      );
    }
    if (error) {
      return <GraphQLError error={error}>Update failed</GraphQLError>;
    }
    return null;
  };

  const renderButton = () => {
    const disabled = loading || !isValid;
    return (
      <StyledButton disabled={disabled} variant="contained" onClick={save} sx={{ mt: 3 }}>
        Save changes
      </StyledButton>
    );
  };

  React.useEffect(() => {
    const valid = !(
      publicProfileData.facebookError ||
      publicProfileData.twitterError ||
      publicProfileData.tiktokError ||
      publicProfileData.discordError ||
      publicProfileData.instagramError ||
      publicProfileData.displayNameError ||
      publicProfileData.youtubeError
    );
    setIsValid(valid);
  }, [publicProfileData]);

  return (
    <>
      <ContentHeader title="Profile" />
      <Container>
        <div style={{ padding: spacing(1, 3) }}>
          <StyledHeaderText variant="h2">Social links</StyledHeaderText>
          <div style={{ fontSize: 16, marginBottom: spacing(2), color: COLORS.white[60] }}>
            The provided info will be used to display your social media accounts on our website.
          </div>
          <Social publicProfileData={publicProfileData} updatePublicProfileDataFn={setPublicProfileData} />
          {renderButton()}
          <Divider sx={{ my: 4 }} />
          <Account profile={profile} />
          <Divider sx={{ my: 4 }} />
          {renderNotification()}
          <EmailPreferences profile={profile} />
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default Profile;
